import { createState } from "@/global/store/zustand_store";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { useTheme, Box, Grid, Stack, Typography } from "@mui/material";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { GenericCounter } from "@/shared/base/components/GenericCounter/GenericCounter";
import { Divider } from "@/shared/base/components/Divider/Divider";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useTicketTransfer } from "./useTicketTransfer";
import { EmailOrPhoneTextField } from "@/shared/base/components/FormComponents/EmailOrPhoneTextField";
import { CompactTextField } from "@/shared/base/components/FormComponents/CompactTextField";
import { TAddRecipientFormData } from "./types";
import { throttleTime } from "@/global/utils/utils";

export const useAddRecipientModal = createState({
  isOpen: false,
});
export function AddRecipientModal() {
  const theme = useTheme();
  const { md: mdUp } = useCssMediaQuery("up");
  const { isOpen, set } = useAddRecipientModal();
  const formMemthods = useForm({
    defaultValues: { seatCount: 1, sendToFullName: "" },
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { onAddRecipientSubmit, maxSeatCountAvailable } = useTicketTransfer();
  const seatCountField = { name: "seatCount", initialValue: 1 };

  const onClose = () => {
    set({ isOpen: false });
    throttleTime(500, () => {
      formMemthods.reset();
    });
  };

  const onChangeSeatCount = (value: number) => {
    formMemthods.setValue("seatCount", value);
  };

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    const finalData = {} as TAddRecipientFormData;
    finalData.seatCount = data?.seatCount;

    if (data.sendToFullName) finalData.sendToFullName = data?.sendToFullName;

    const emailOrPhoneNumber = data?.emailOrPhoneNumber;
    const isPhoneNumber = /^\d+$/.test(emailOrPhoneNumber);

    if (isPhoneNumber) finalData.sendToPhoneNumber = emailOrPhoneNumber;
    else finalData.sendToEmail = emailOrPhoneNumber;

    await onAddRecipientSubmit(finalData);
    setIsSubmitting(false);
    onClose();
  };

  return (
    <NotificationModal
      isOpen={isOpen}
      hideTitleIcon
      onClose={() => onClose()}
      onClickSecondaryButton={() => onClose()}
      title="Add Ticket Recipient"
      primaryButtonText="Add Recipient"
      secondaryButtonText="Cancel"
      renderFooter={() => <></>}
      renderBody={() => {
        return (
          <Box>
            <FormProvider {...formMemthods}>
              <form onSubmit={formMemthods.handleSubmit(onSubmit)}>
                <Box>
                  <Box p={mdUp ? 4 : 3} pb={4}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <CompactTextField
                          label="Recipient Name (Optional)"
                          {...formMemthods.register("sendToFullName")}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <EmailOrPhoneTextField label="Recipient Email or Phone Number" />
                      </Grid>

                      <Grid item xs={12}>
                        <Stack alignItems={"center"} spacing={3} mt={2}>
                          <Stack width={"100%"} position={"relative"}>
                            <Box
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                backgroundColor:
                                  theme.palette.background.default,
                                zIndex: 1,
                                padding: "4px 12px",
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                color="text.primary"
                                fontSize={"0.9rem"}
                              >
                                Number of Seats
                              </Typography>
                            </Box>
                            <Divider />
                          </Stack>
                          <Box width={"100%"}>
                            <GenericCounter
                              value={formMemthods.watch("seatCount")}
                              max={maxSeatCountAvailable || 0}
                              min={1}
                              onChange={onChangeSeatCount}
                              slot={{
                                wrapper: {
                                  sx: { width: "100%" },
                                },
                                value: {
                                  sx: {
                                    flexGrow: 1,
                                    height: "40px",
                                  },
                                },
                                increment: {
                                  sx: {
                                    width: "48px",
                                    height: "40px",
                                  },
                                },
                                decrement: {
                                  sx: {
                                    width: "48px",
                                    height: "40px",
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider />
                  <Stack
                    p={2}
                    direction={"row"}
                    justifyContent={"flex-end"}
                    spacing={1.5}
                  >
                    <ModalActionButton
                      isDisabled={isSubmitting}
                      variant="secondary"
                      style={{
                        width: mdUp ? "inherit" : "100%",
                        paddingLeft: mdUp ? "16px" : "4px",
                        paddingRight: mdUp ? "16px" : "4px",
                      }}
                      onClick={onClose}
                    >
                      Cancel
                    </ModalActionButton>
                    <ModalActionButton
                      isLoading={isSubmitting}
                      isDisabled={isSubmitting}
                      type="submit"
                      startIcon={
                        <AddOutlinedIcon
                          style={{
                            fontSize: mdUp ? "22px" : "18px",
                          }}
                        />
                      }
                      sx={{
                        width: mdUp ? "inherit" : "100%",
                        paddingLeft: mdUp ? "16px" : "4px",
                        paddingRight: mdUp ? "16px" : "4px",
                        "& .MuiButton-icon": {
                          marginRight: mdUp ? "4px" : "0px",
                        },
                      }}
                    >
                      Initiate Transfer
                    </ModalActionButton>
                  </Stack>
                </Box>
              </form>
            </FormProvider>
          </Box>
        );
      }}
    />
  );
}
